import React, { useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Segmented,
  Space,
  Typography,
  message,
} from "antd";
import { login, signup } from "../services/auth";

const { Title, Text } = Typography;

/**
 * Sign in / Create account.
 *
 * Signup is open, but a new account has no access: invited emails are approved
 * automatically on first load; everyone else lands on the "waiting for
 * approval" screen until an admin lets them in (or invites them later — the
 * invite redeems on their next visit).
 */
const AuthPage = ({ onAuthComplete }) => {
  const [mode, setMode] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const handleEmailLogin = async (values) => {
    setLoading(true);
    try {
      const payload = await login({
        email: values.email,
        password: values.password,
      });
      onAuthComplete?.(payload);
    } catch (error) {
      messageApi.error(error?.payload?.error || error?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (values) => {
    setLoading(true);
    try {
      const payload = await signup({
        email: values.email,
        password: values.password,
        profile: { name: values.name || "" },
      });
      // Rare: account created but auto-login didn't stick — a manual sign-in
      // will work.
      if (!payload?.session) {
        messageApi.success("Account created — sign in to continue.");
        setMode("signin");
        return;
      }
      // Session established, no email confirmation needed. The app decides
      // what they see next: invited emails go straight in; everyone else gets
      // the pending screen.
      onAuthComplete?.(payload);
    } catch (error) {
      if (error?.payload?.code === "ACCOUNT_EXISTS" || error?.status === 409) {
        messageApi.info("You already have an account — sign in instead.");
        setMode("signin");
        return;
      }
      if (error?.payload?.code === "NOT_INVITED") {
        // Refused before anything was created — they stay right here.
        messageApi.warning(
          error?.payload?.error ||
            "This workspace is invite-only. Ask an admin to invite your email first.",
          6,
        );
        return;
      }
      messageApi.error(
        error?.payload?.error || error?.message || "Signup failed",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {contextHolder}
      <Card className="auth-card" variant="borderless">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Title level={3} style={{ marginBottom: 4 }}>
              Welcome
            </Title>
            <Text type="secondary">
              {mode === "signin"
                ? "Sign in to access your workspace."
                : "Create an account. If you've been invited, you'll get access immediately."}
            </Text>
          </div>

          <Segmented
            block
            value={mode}
            onChange={setMode}
            options={[
              { label: "Sign in", value: "signin" },
              { label: "Create account", value: "signup" },
            ]}
          />

          {mode === "signin" ? (
            <Form
              className="auth-form"
              layout="vertical"
              onFinish={handleEmailLogin}
              autoComplete="on"
            >
              <Form.Item
                label="Email"
                name="email"
                rules={[{ required: true, message: "Email required" }]}
              >
                <Input type="email" name="email" autoComplete="email" />
              </Form.Item>
              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: "Password required" }]}
              >
                <Input.Password
                  name="password"
                  autoComplete="current-password"
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Sign In
              </Button>
            </Form>
          ) : (
            <Form
              className="auth-form"
              layout="vertical"
              onFinish={handleSignup}
              autoComplete="on"
            >
              <Form.Item label="Name" name="name">
                <Input name="name" autoComplete="name" />
              </Form.Item>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Email required" },
                  { type: "email", message: "That doesn't look like an email" },
                ]}
              >
                <Input type="email" name="email" autoComplete="email" />
              </Form.Item>
              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: "Password required" },
                  { min: 8, message: "At least 8 characters" },
                ]}
              >
                <Input.Password name="password" autoComplete="new-password" />
              </Form.Item>
              <Form.Item
                label="Confirm password"
                name="confirm"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords don't match"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password name="confirm" autoComplete="new-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Create account
              </Button>
              <Text
                type="secondary"
                style={{
                  display: "block",
                  marginTop: 10,
                  fontSize: 12,
                  textAlign: "center",
                }}
              >
                This workspace is invite-only — your email must be invited by
                an admin before you can create an account.
              </Text>
            </Form>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default AuthPage;
